import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useData } from '../context/DataContext';

const ImportExcel = () => {
  const { importTolteckData } = useData();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [importedData, setImportedData] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    setMessage('');
    setMessageType('info');

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];

          // Get all rows as arrays, using the 7th row (index 6) as headers
          // Rows 0-5 are metadata/filters, row 6 is the actual header
          const rowsWithHeaders = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

          // Skip first 6 rows (metadata, filters, empty), use row 6 as headers
          const headerRow = rowsWithHeaders[6];
          if (!headerRow || headerRow.length === 0) {
            setMessage('Format de fichier Excel non reconnu');
            setMessageType('error');
            setLoading(false);
            return;
          }

          // Build data rows from rows 7 onwards, mapping to header names
          const jsonData = rowsWithHeaders.slice(7).map((row, index) => {
            const rowObj: any = {};
            headerRow.forEach((header, i) => {
              // Map Excel column names to expected import fields
              const value = row[i];
              rowObj[header] = value;
            });
            return rowObj;
          });

          // Filter rows that have a valid document number
          const filteredData = jsonData.filter(row =>
            row['Numéro de document'] &&
            row['Numéro de document'] !== 'Numéro de document'
          );

          setImportedData(filteredData);
          setMessage(`${filteredData.length} lignes chargées. Cliquez sur "Importer" pour les intégrer.`);
          setMessageType('info');
        } catch (error) {
          setMessage('Erreur lors de la lecture du fichier: ' + error.message);
          setMessageType('error');
        }
        setLoading(false);
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      setMessage('Erreur: ' + error.message);
      setMessageType('error');
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!importedData || importedData.length === 0) {
      setMessage('Aucune donnée à importer');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      const result = await importTolteckData(importedData);
      setMessage(result.message);
      setMessageType(result.success ? 'success' : 'error');
      if (result.success) {
        setImportedData(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      setMessage('Erreur lors de l\'import: ' + error.message);
      setMessageType('error');
    }
    setLoading(false);
  };

  return (
    <section className="import-section" aria-label="Import des données Tolteck">
      <h3>📥 Import des données Tolteck</h3>
      <div className="import-container">
        <div className="file-input-group">
          <input
            ref={fileInputRef}
            id="fileInput"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            disabled={loading}
            aria-label="Choisir un fichier Excel"
          />
          <label htmlFor="fileInput">
            {loading ? 'Chargement...' : '📁 Choisir un fichier Excel'}
          </label>
          {importedData && (
            <button
              onClick={handleImport}
              disabled={loading}
              className="btn-import"
              aria-label="Importer les données"
            >
              {loading ? 'Import en cours...' : '📥 Importer les données'}
            </button>
          )}
        </div>

        {message && (
          <div className={`import-message ${messageType}`} role="status">
            {message}
          </div>
        )}

        {importedData && importedData.length > 0 && (
          <div className="import-preview">
            <p><strong>Aperçu :</strong> {importedData.length} lignes à importer</p>
            <div className="preview-table-container" role="table" aria-label="Aperçu des données à importer">
              <table className="preview-table">
                <thead>
                  <tr>
                    <th>N° document</th>
                    <th>Date</th>
                    <th>Client</th>
                    <th>Chantier</th>
                    <th>Montant HT</th>
                    <th>Statut</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {importedData.slice(0, 5).map((row, index) => (
                    <tr key={index}>
                      <td>{row['Numéro de document']}</td>
                      <td>{row['Date']}</td>
                      <td>{row['Nom du client']}</td>
                      <td>{row['Nom du chantier']}</td>
                      <td>{row['Montant HT']}</td>
                      <td>{row['Statut']}</td>
                      <td>{row['Type']}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {importedData.length > 5 && (
                <p className="preview-note">... et {importedData.length - 5} autres lignes</p>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ImportExcel;